<script setup lang="ts">
/**
 * 应用根组件（F01 + F13 全局 navbox chrome + F17 全局 SiteHeader / SiteFooter）。
 *
 * `<AnchorNav>` 提升为全局 chrome（ADR-0014）：首页 + 详情页都可见，对齐
 * `project/` 模板 navbox 在 index/detail 页均出现的结构。AnchorNav 数据由
 * Showcase Store aggregate 驱动（navItems 树 + sections + previewToken），
 * 详情页路由 detail key 解析为真实 sectionId 后传入，驱动 detail 激活态。
 *
 * **F17 全局 chrome**（ADR-0017）：`<SiteHeader>` / `<SiteFooter>` 挂在
 * RouterView 上下，首页 + 详情页共享一套页面 chrome（header / footer /
 * 背景图）。SiteHeader 透传 siteConfig，SiteFooter 仅保留模板背景与 logo。
 *
 * **activeAnchorId 共享 ref**（F13）：App 持有并 provide，HomePage 的
 * IntersectionObserver 写入，AnchorNav 读取驱动首页区高亮。用 provide/inject
 * 而非 store，避免瞬时 UI 滚动状态耦合进 Showcase Store。
 */
import { computed, onBeforeUnmount, onMounted, provide, ref } from 'vue';
import { RouterView, useRoute } from 'vue-router';

import AnchorNav from '@/components/AnchorNav.vue';
import SiteHeader from '@/components/SiteHeader.vue';
import SiteFooter from '@/components/SiteFooter.vue';
import { useShowcase, ACTIVE_ANCHOR_ID_KEY } from '@/store/use-showcase';
import { selectAnchorNavTree } from '@/composables/anchor-nav-tree';
import { findSectionByDetailKey } from '@/composables/detail-path';
import { homeAnchorIds } from '@/composables/home-anchor-ids';

const store = useShowcase();
const route = useRoute();

const activeAnchorId = ref('');
provide(ACTIVE_ANCHOR_ID_KEY, activeAnchorId);

const aggregate = computed(() => store.state.aggregate);
const navItems = computed(() => aggregate.value?.navItems ?? []);
const sections = computed(() => aggregate.value?.sections ?? []);
const previewToken = computed(() => store.state.previewToken);
const siteConfig = computed(() => aggregate.value?.siteConfig ?? null);

const sectionAnchorIds = computed(() => homeAnchorIds(aggregate.value));
const anchorNavItems = computed(() => selectAnchorNavTree(navItems.value, sectionAnchorIds.value));

const activeSectionId = computed(() => {
  if (route.name !== 'detail') {
    return null;
  }
  const value = route.params.sectionId;
  if (typeof value !== 'string') {
    return null;
  }
  return findSectionByDetailKey(sections.value, value)?.id ?? value;
});

onMounted(() => {
  window.addEventListener('focus', revalidateOnReturn);
  document.addEventListener('visibilitychange', revalidateOnVisible);
});

onBeforeUnmount(() => {
  window.removeEventListener('focus', revalidateOnReturn);
  document.removeEventListener('visibilitychange', revalidateOnVisible);
});

function revalidateOnReturn(): void {
  if (store.state.status === 'ready') {
    void store.revalidate();
  }
}

function revalidateOnVisible(): void {
  if (document.visibilityState === 'visible') {
    revalidateOnReturn();
  }
}
</script>

<template>
  <SiteHeader :site-config="siteConfig" />
  <AnchorNav
    :items="anchorNavItems"
    :sections="sections"
    :preview-token="previewToken"
    :active-section-id="activeSectionId"
  />
  <RouterView />
  <SiteFooter />
</template>
